/**
 * Filter utility functions
 * Functions for filtering table data based on various criteria
 */

export const NAME_FILTER_PARAM = 'filter';

export const parseNameFilterParams = (params: URLSearchParams): string[] =>
  params
    .getAll(NAME_FILTER_PARAM)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

export const describeNameFilters = (names: string[]): string =>
  names.length ? names.join(', ') : '';

export interface TableItem {
  college?: {
    displayName?: string;
  };
  conference?: string;
  /** Player leaderboard rows */
  name?: string;
}

/**
 * Names to search on a row (player name, college, or conference).
 */
const extractSearchableNames = (item: TableItem): string[] => {
  const names: string[] = [];
  if (item.name && typeof item.name === 'string') {
    names.push(item.name);
  }
  if (item.college?.displayName) {
    names.push(item.college.displayName);
  }
  if (item.conference) {
    names.push(item.conference);
  }
  return names;
};

/**
 * Checks if an item matches any of the provided filters
 * @param item - The data item to check
 * @param filters - Array of filter strings to match against
 * @returns True if the item matches any filter
 */
export const matchesFilters = (item: TableItem, filters: string[]): boolean => {
  if (!filters || filters.length === 0) {
    return true;
  }

  const names = extractSearchableNames(item);
  if (names.length === 0) {
    return false;
  }

  const lowerNames = names.map((n) => n.toLowerCase());

  return filters.some((filter) => {
    const lowerFilter = filter.toLowerCase();
    return lowerNames.some((name) => name.indexOf(lowerFilter) >= 0);
  });
};

/**
 * Filters an array of data items based on provided filters
 * @param data - Array of data items to filter
 * @param filters - Array of filter strings
 * @param shouldFilter - Whether filtering should be applied
 * @returns Filtered array of data items
 */
export const filterTableData = <T extends TableItem>(
  data: T[] | null | undefined,
  filters: string[],
  shouldFilter: boolean = true
): T[] => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  if (!shouldFilter || !filters || filters.length === 0) {
    return data;
  }

  return data.filter((item) => matchesFilters(item, filters));
};

